import React, { useState, useMemo, useEffect, useCallback } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import editImage from "../../assets/images/icons/edit-2.svg";
import { toast } from "react-toastify";
import { Button } from "react-bootstrap";
import { UI_CONSTANTS } from "../../utils/constants";
import TablePagination from "../../commons/Pagination/TablePagination";
import LocationPincodeModal from "./LocationPincodeModal";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { mapPincodeListRow, parsePincodeListResponse } from "../../utils/cityList";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

const emptyModalState = () => ({
  show: false,
  mode: "add",
  row: null,
});

function ManagePincodes() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modal, setModal] = useState(emptyModalState());

  const fetchPincodeList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const trimmedSearch = search.trim();
      if (trimmedSearch) {
        params.set("search", trimmedSearch);
      }

      const query = params.toString();
      const url = query ? `${endpoints.pincodeList}?${query}` : endpoints.pincodeList;
      const response = await handleApiCall("get", url);

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const { list: items } = parsePincodeListResponse(response?.data?.data);
        setList(items.map(mapPincodeListRow));
      } else {
        setList([]);
        toast.error(response?.data?.message || "Failed to load pincodes");
      }
    } catch (error) {
      console.error("Error fetching pincode list:", error);
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPincodeList();
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [fetchPincodeList]);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));

  const data = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return list.slice(start, start + PAGE_SIZE);
  }, [list, page]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const openAddModal = () => {
    setModal({ show: true, mode: "add", row: null });
  };

  const openEditModal = (row) => {
    setModal({ show: true, mode: "edit", row });
  };

  const closeModal = () => {
    if (submitting) return;
    setModal(emptyModalState());
  };

  const handleModalSubmit = async ({ city, location, pincode }) => {
    const trimmedLocation = location.trim();
    const trimmedPincode = pincode.trim();

    if (!trimmedLocation || !trimmedPincode) {
      toast.error("Location and pincode are required");
      return;
    }

    setSubmitting(true);
    try {
      let response;
      const isEdit = modal.mode === "edit" && modal.row;

      if (isEdit) {
        const pincodeRecordId =
          modal.row.pincodeId ||
          modal.row.raw?._id ||
          modal.row.raw?.id ||
          modal.row.raw?.pincode_id;

        if (!pincodeRecordId) {
          toast.error("Unable to update: pincode id is missing from list data");
          setSubmitting(false);
          return;
        }

        response = await handleApiCall("patch", `${endpoints.updatePincode}/${pincodeRecordId}`, {
          location: trimmedLocation,
          pincode: trimmedPincode,
        });
      } else {
        if (!city?.id) {
          toast.error("Please select a city");
          setSubmitting(false);
          return;
        }

        response = await handleApiCall("post", endpoints.addPincode, {
          city_id: city.id,
          location: trimmedLocation,
          pincode: trimmedPincode,
        });
      }

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        toast.success(modal.mode === "edit" ? "Pincode updated successfully" : "Pincode added successfully");
        setModal(emptyModalState());
        fetchPincodeList();
      } else {
        toast.error(response?.data?.message || "Failed to save pincode");
      }
    } catch (error) {
      console.error("Error saving pincode:", error);
      toast.error("Failed to save pincode");
    } finally {
      setSubmitting(false);
    }
  };

  const modalInitialCity =
    modal.mode === "edit" && modal.row
      ? { id: modal.row.cityId, name: modal.row.cityName }
      : { id: "", name: "" };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Manage Pincode</h3>
            </div>
            <div className="mb-4 px-0 py-2">
              <div className="card-heading d-flex align-items-center mb-3 pb-2">
                <div className="d-flex align-items-center col-md-12 px-0">
                  <form className="col-md-3 px-0" onSubmit={(e) => e.preventDefault()}>
                    <div className="form-group my-0">
                      <input
                        className="form-control search-icon left-icon border"
                        placeholder="Search city, location or pincode"
                        value={search}
                        onChange={handleSearch}
                      />
                    </div>
                  </form>
                  <Button type="button" className="ml-auto btn btn-primary" onClick={openAddModal}>
                    Add New +
                  </Button>
                </div>
              </div>
              <div className="col-md-12 tab-content row px-0 mx-0">
                <div className="table-responsive px-0">
                  <table className="table table-striped trim">
                    <thead>
                      <tr>
                        <th width="80" className="text-center">
                          S No
                        </th>
                        <th>City</th>
                        <th>Location</th>
                        <th>Pincode</th>
                        <th width="120" className="text-center">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="5" className="text-center py-4">
                            Loading...
                          </td>
                        </tr>
                      ) : data.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-4">
                            {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                          </td>
                        </tr>
                      ) : (
                        data.map((item, index) => (
                          <tr key={item.rowId}>
                            <td className="text-center">{(page - 1) * PAGE_SIZE + index + 1}</td>
                            <td className="text-center">{item.cityName}</td>
                            <td className="text-center">{item.locationName}</td>
                            <td className="text-center">{item.pincode}</td>
                            <td>
                              <div className="d-flex align-items-center justify-content-center">
                                <button
                                  type="button"
                                  className="btn btn-link p-0 mx-2"
                                  onClick={() => openEditModal(item)}
                                  aria-label="Edit"
                                >
                                  <img src={editImage} alt="Edit" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {!loading && list.length > 0 && (
                <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />
              )}
            </div>
          </div>
        </div>
      </div>

      <LocationPincodeModal
        show={modal.show}
        onHide={closeModal}
        mode={modal.mode}
        submitting={submitting}
        initialCity={modalInitialCity}
        initialLocation={modal.row?.locationName || ""}
        initialPincode={modal.row?.pincode || ""}
        onSubmit={handleModalSubmit}
      />
    </div>
  );
}

export default ManagePincodes;
